import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext.tsx";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext.tsx";
import { ThemeProvider } from "./contexts/ThemeContext.tsx";
import { AmountUnitProvider } from "./contexts/AmountUnitContext.tsx";
import ProtectedRoute from "./components/ProtectedRoute.tsx";
import Dashboard from "./pages/Dashboard.tsx";
import Reports from "./pages/Reports.tsx";
import AnnualLedger from "./pages/AnnualLedger.tsx";
import LifePlan from "./pages/LifePlan.tsx";
import Transactions from "./pages/Transactions.tsx";
import Accounts from "./pages/Accounts.tsx";
import CreditCards from "./pages/CreditCards.tsx";
import Budget from "./pages/Budget.tsx";
import Recurring from "./pages/Recurring.tsx";
import Investments from "./pages/Investments.tsx";
import Settings from "./pages/Settings.tsx";
import Login from "./pages/Login.tsx";
import Register from "./pages/Register.tsx";

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed'

function SidebarContent({ expanded, collapsed, onToggleCollapse, onNavigate }: {
  expanded: boolean
  collapsed: boolean
  onToggleCollapse?: () => void
  onNavigate?: () => void
}) {
  const { user, logout } = useAuth()
  const { t } = useLanguage()

  const navItem = (to: string, end: boolean, icon: string, label: string) => (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      title={expanded ? undefined : label}
      aria-label={label}
      className={({ isActive }) =>
        `flex items-center gap-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          expanded ? 'px-3' : 'px-0 justify-center'
        } ${
          isActive ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
        }`
      }
    >
      <span className="shrink-0 w-5 text-center">{icon}</span>
      {expanded && <span className="truncate">{label}</span>}
    </NavLink>
  )

  return (
    <>
      <div className={`h-12 flex items-center border-b border-gray-200 dark:border-gray-700 ${expanded ? 'px-5 justify-between' : 'px-0 justify-center'}`}>
        {expanded && <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">Zeni</span>}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            aria-label={collapsed ? t('nav_expand_sidebar') : t('nav_collapse_sidebar')}
            title={collapsed ? t('nav_expand_sidebar') : t('nav_collapse_sidebar')}
            data-testid="sidebar-toggle"
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="4" width="18" height="16" rx="2" strokeWidth={2} />
              <path strokeWidth={2} d={collapsed ? 'M15 4v16' : 'M9 4v16'} />
            </svg>
          </button>
        )}
      </div>
      <nav className={`flex-1 py-2 space-y-0.5 overflow-y-auto overflow-x-hidden ${expanded ? 'px-3' : 'px-2'}`}>
        {navItem('/', true, '🏠', t('nav_dashboard'))}
        {navItem('/reports', false, '📊', t('nav_reports'))}
        {navItem('/calendar', false, '📅', t('nav_calendar'))}
        {navItem('/life-plan', false, '🧭', t('nav_life_plan'))}
        {navItem('/transactions', false, '📝', t('nav_transactions'))}
        {navItem('/accounts', false, '🏦', t('nav_accounts'))}
        {navItem('/investments', false, '📈', t('nav_investments'))}
        {navItem('/credit-cards', false, '💳', t('nav_credit_cards'))}
        {navItem('/budget', false, '💰', t('nav_budget'))}
        {navItem('/recurring', false, '🔄', t('nav_recurring'))}
        {navItem('/settings', false, '⚙️', t('nav_settings'))}
      </nav>
      <div className={`py-4 border-t border-gray-200 dark:border-gray-700 ${expanded ? 'px-4' : 'px-2'}`}>
        {expanded ? (
          <>
            {user?.displayName && (
              <p className="text-xs font-medium text-gray-700 truncate mb-1 dark:text-gray-300">{user.displayName}</p>
            )}
            {user?.email && (
              <p className="text-xs text-gray-400 truncate mb-2 dark:text-gray-500">{user.email}</p>
            )}
            <button
              onClick={() => { void logout() }}
              data-testid="logout-button"
              className="text-xs text-gray-500 hover:text-red-600 transition-colors dark:text-gray-400"
            >
              {t('nav_logout')}
            </button>
          </>
        ) : (
          <button
            onClick={() => { void logout() }}
            data-testid="logout-button"
            aria-label={t('nav_logout')}
            title={t('nav_logout')}
            className="w-full flex justify-center py-1 text-gray-500 hover:text-red-600 transition-colors dark:text-gray-400"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 8H6a2 2 0 01-2-2V6a2 2 0 012-2h7" />
            </svg>
          </button>
        )}
      </div>
    </>
  )
}

function AppLayout({ children }: { children: React.ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  )
  const [hovered, setHovered] = useState(false)
  const location = useLocation()

  // 折りたたみ中はホバーで一時的に開く
  const expanded = !collapsed || hovered

  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next))
      // 展開状態へ戻したときにホバー由来の一時展開を持ち越さない
      if (!next) setHovered(false)
      return next
    })
  }

  return (
    <div className="h-dvh bg-gray-50 flex dark:bg-gray-950">
      {/* Desktop sidebar: 折りたたみ時は幅を維持したままオーバーレイで開くのでコンテンツがずれない */}
      <div className={`hidden md:block shrink-0 transition-[width] duration-200 ${collapsed ? 'w-16' : 'w-56'}`} />
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        data-testid="sidebar"
        data-collapsed={collapsed}
        className={`hidden md:flex fixed inset-y-0 left-0 z-40 bg-white border-r border-gray-200 flex-col transition-[width] duration-200 dark:bg-gray-900 dark:border-gray-700 ${
          expanded ? 'w-56' : 'w-16'
        } ${collapsed && hovered ? 'shadow-xl' : ''}`}
      >
        <SidebarContent expanded={expanded} collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white border-r border-gray-200 flex flex-col dark:bg-gray-900 dark:border-gray-700">
            <SidebarContent expanded collapsed={false} onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Mobile top bar */}
        <header className="md:hidden h-14 flex items-center px-4 bg-white border-b border-gray-200 dark:bg-gray-900 dark:border-gray-700">
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span className="ml-3 text-lg font-bold text-indigo-600 dark:text-indigo-400">Zeni</span>
        </header>
        <main className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4">
          {children}
        </main>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <LanguageProvider>
      <AmountUnitProvider>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/calendar" element={<AnnualLedger />} />
                    <Route path="/life-plan" element={<LifePlan />} />
                    {/* 旧パス。収支はライフプランに統合したので、そちらへ送る */}
                    <Route path="/balance" element={<Navigate to="/life-plan" replace />} />
                    <Route path="/annual" element={<Navigate to="/life-plan" replace />} />
                    <Route path="/transactions" element={<Transactions />} />
                    <Route path="/accounts" element={<Accounts />} />
                    <Route path="/credit-cards" element={<CreditCards />} />
                    <Route path="/budget" element={<Budget />} />
                    <Route path="/recurring" element={<Recurring />} />
                    <Route path="/investments" element={<Investments />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </AppLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
      </AmountUnitProvider>
      </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}
